import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  skip,
  tap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { DataService } from './data.service';
import { applyReservationsStackPaths } from '../shared/sidebar/menu-items-v2';
import { unwrapNewApiEnvelope } from '../shared/utils/new-api-envelope.util';

type ReservationsV2FeatureResponse = {
  v2: boolean;
};

/**
 * CO-646 — one probe per building, then stamp áreas/reservas paths (v1|v2).
 * Fail-closed until the features endpoint responds.
 */
@Injectable({ providedIn: 'root' })
export class ReservationsStackFeatureService implements OnDestroy {
  private readonly _v2$ = new BehaviorSubject<boolean>(false);
  private readonly _ready$ = new BehaviorSubject<boolean>(false);
  private readonly _buildingSub: Subscription;

  /** Building id of the last successful (or fail-closed) probe. */
  private _probedBuildingId: string | null = null;
  /** Shared in-flight HTTP for the building currently being probed. */
  private _inflight$: Observable<boolean> | null = null;
  private _inflightBuildingId: string | null = null;

  readonly v2$: Observable<boolean> = this._v2$.asObservable().pipe(distinctUntilChanged());
  readonly ready$: Observable<boolean> = this._ready$.asObservable().pipe(distinctUntilChanged());

  constructor(
    private readonly _http: HttpClient,
    private readonly _dataService: DataService,
    private readonly _router: Router,
  ) {
    applyReservationsStackPaths(false);

    // buildingChanged$ is a BehaviorSubject — record initial buildingId in distinctUntilChanged
    // and skip the seed emission so same-building setUser() refreshes do not re-probe.
    this._buildingSub = this._dataService
      .buildingChanged$()
      .pipe(
        map(() => this._buildingId()),
        distinctUntilChanged(),
        skip(1),
      )
      .subscribe((buildingId) => {
        if (!buildingId) {
          this._resetFailClosed();
          return;
        }
        this._probe(buildingId).subscribe();
      });

    const initialId = this._buildingId();
    if (initialId) {
      this._probe(initialId).subscribe();
    } else {
      this._ready$.next(true);
    }
  }

  ngOnDestroy(): void {
    this._buildingSub.unsubscribe();
  }

  get isV2(): boolean {
    return this._v2$.value;
  }

  get isReady(): boolean {
    return this._ready$.value;
  }

  /**
   * Waits for the single probe of the current building (reuses in-flight / cache).
   * Does not start a second HTTP if one already ran for this building.
   */
  whenReady(): Observable<boolean> {
    const buildingId = this._buildingId();
    if (!buildingId) {
      return of(false);
    }
    if (this._probedBuildingId === buildingId && this._ready$.value) {
      return of(this.isV2);
    }
    return this._probe(buildingId);
  }

  /**
   * @deprecated Prefer whenReady() — kept so AppComponent / callers do not start extra probes.
   * No-ops when the current building was already probed.
   */
  refresh(): Observable<boolean> {
    return this.whenReady();
  }

  areasListUrl(): string {
    return this.isV2 ? '/app/areas/v2' : '/app/areas';
  }

  reservationsListUrl(): string {
    return this.isV2 ? '/app/reservations/v2' : '/app/reservations';
  }

  areasMacroListUrl(): string {
    return this.isV2 ? '/app-community/areas/v2' : '/app-community/areas';
  }

  reservationsMacroListUrl(): string {
    return this.isV2 ? '/app-community/reservations/v2' : '/app-community/reservations';
  }

  resolveMenuPath(path: string): string {
    if (!path) {
      return path;
    }
    const normalized = path.split('?')[0];
    if (normalized === '/app/reservations' || normalized === '/app/reservations/v2') {
      return this.reservationsListUrl();
    }
    if (normalized === '/app/areas' || normalized === '/app/areas/v2') {
      return this.areasListUrl();
    }
    if (normalized === '/app-community/reservations' || normalized === '/app-community/reservations/v2') {
      return this.reservationsMacroListUrl();
    }
    if (normalized === '/app-community/areas' || normalized === '/app-community/areas/v2') {
      return this.areasMacroListUrl();
    }
    return path;
  }

  private _buildingId(): string | null {
    try {
      const building = this._dataService.getBuilding();
      const id = building?.id ?? building?._id;
      return id ? String(id) : null;
    } catch {
      return null;
    }
  }

  private _probe(buildingId: string): Observable<boolean> {
    if (this._probedBuildingId === buildingId && this._ready$.value) {
      return of(this.isV2);
    }

    if (this._inflight$ && this._inflightBuildingId === buildingId) {
      return this._inflight$;
    }

    this._ready$.next(false);
    this._inflightBuildingId = buildingId;

    const scope = this._dataService.isProprietorOnly() ? 'proprietor' : 'admin';
    const url = `${environment.newApiBackend}/api/v2/reservations/${scope}/features/reservations-v2`;

    this._inflight$ = this._http.get<unknown>(url).pipe(
      map((body) => unwrapNewApiEnvelope<ReservationsV2FeatureResponse>(body)?.v2 === true),
      catchError(() => of(false)),
      tap((v2) => this._apply(v2, buildingId)),
      finalize(() => {
        this._inflight$ = null;
        this._inflightBuildingId = null;
      }),
      shareReplay(1),
    );

    return this._inflight$;
  }

  private _resetFailClosed(): void {
    this._probedBuildingId = null;
    this._inflight$ = null;
    this._inflightBuildingId = null;
    applyReservationsStackPaths(false);
    this._v2$.next(false);
    this._ready$.next(true);
  }

  private _apply(v2: boolean, buildingId: string): void {
    const previous = this._v2$.value;
    applyReservationsStackPaths(v2);
    this._patchBuildingReservationV2(v2);
    this._probedBuildingId = buildingId;
    this._v2$.next(v2);
    this._ready$.next(true);

    if (previous !== v2) {
      this._redirectIfOnStackUrl(v2);
    }
  }

  private _redirectIfOnStackUrl(v2: boolean): void {
    const url = this._router.url.split('?')[0];
    const isMacro = url.startsWith('/app-community');
    const isAreas = /(^|\/)areas(\/|$)/.test(url);
    const isReservations = /(^|\/)reservations(\/|$)/.test(url);
    if (!isAreas && !isReservations) {
      return;
    }

    const onV2Path = /\/areas\/v2(\/|$)/.test(url) || /\/reservations\/v2(\/|$)/.test(url);

    if (v2 && !onV2Path) {
      void this._router.navigateByUrl(
        isAreas
          ? isMacro
            ? '/app-community/areas/v2'
            : '/app/areas/v2'
          : isMacro
            ? '/app-community/reservations/v2'
            : '/app/reservations/v2',
      );
      return;
    }

    if (!v2 && onV2Path) {
      void this._router.navigateByUrl(
        isAreas
          ? isMacro
            ? '/app-community/areas'
            : '/app/areas'
          : isMacro
            ? '/app-community/reservations'
            : '/app/reservations',
      );
    }
  }

  private _patchBuildingReservationV2(v2: boolean): void {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return;
      }
      const user = JSON.parse(raw);
      const building = user?.buildings?.[0];
      if (!building?.config) {
        return;
      }
      building.config.reservation = {
        ...(building.config.reservation || { enable: false }),
        v2,
      };
      localStorage.setItem('currentUser', JSON.stringify(user));
    } catch {
      // ignore
    }
  }
}
